// Piano-driven control surface. Owns the pure
// CommandLayer (fed raw MIDI note events) and the CommandNav cursor over the
// drawer, translating live-strip actions and nav keys into host calls. The host
// (main.ts) stays unaware of MIDI details — it just exposes transport actions.

import {
  CommandLayer,
  type CommandLayerCallbacks,
  DEFAULT_PIANO_CONTROL,
  type LiveAction,
  type NavKey,
} from "../input/command-layer";
import { CommandNav } from "./command-nav";

/** Glyphs drawn on the command-mode D-pad keys (matches DEFAULT_PIANO_CONTROL.navKeys). */
const NAV_GLYPHS: ReadonlyMap<number, string> = new Map([
  [72, "←"], // C5
  [74, "→"], // D5
  [76, "↑"], // E5
  [77, "↓"], // F5
  [79, "●"], // G5 select
  [81, "✕"], // A5 exit
]);
const NO_GLYPHS: ReadonlyMap<number, string> = new Map();

/** What PianoControl needs from the app to act on live + nav events. */
interface PianoControlHost {
  /** The drawer element command mode opens and navigates. */
  menu: HTMLElement;
  /** Whether the live strip is currently enabled (for overlay + config). */
  liveKeysEnabled(): boolean;
  restart(): void;
  loopCycle(): void;
  playPause(): void;
  nudgeTempo(delta: number): void;
  /** Toggle the continuous coach loop on/off (the C♯2 live key). */
  toggleCoach(): void;
  /** Start "play it for me" — the auto listen-through (the D♯2 live key). */
  playForMe(): void;
  /** Toggle the metronome click on/off (the G♯2 live key). */
  toggleMetronome(): void;
  /** Toggle performance recording on/off (the A2 live key). */
  toggleRecord(): void;
  /** Called when entering command mode (pause transport, etc.). */
  onEnterCommandMode(): void;
  /** Intercept a nav key (e.g. the map is open). Return true if consumed. */
  onNav?(key: NavKey): boolean;
  /** Called when command mode exits (so the host can close transient surfaces). */
  onExitCommandMode?(): void;
}

/**
 * Owns the piano-driven control surface. Translates live-strip actions and nav
 * keys into host calls and exposes a read-only overlay + command-mode flag for
 * the renderer; the engine never learns it exists.
 */
export class PianoControl {
  private readonly nav: CommandNav;
  private readonly layer: CommandLayer;
  private active = false;

  constructor(private readonly host: PianoControlHost) {
    this.nav = new CommandNav(host.menu);
    const callbacks: CommandLayerCallbacks = {
      onLiveAction: (action) => this.runLiveAction(action),
      onEnterCommandMode: () => {
        this.active = true;
        host.onEnterCommandMode();
        this.nav.open();
      },
      onExitCommandMode: () => {
        this.active = false;
        this.nav.close();
        host.onExitCommandMode?.();
      },
      onNav: (key: NavKey) => {
        if (this.host.onNav?.(key)) return;
        this.nav.handle(key);
      },
    };
    this.layer = new CommandLayer(
      { ...DEFAULT_PIANO_CONTROL, liveKeysEnabled: host.liveKeysEnabled() },
      callbacks,
    );
  }

  get commandMode(): boolean {
    return this.active;
  }

  /** Feed a raw MIDI note event; returns true if it must be suppressed from play. */
  note(pitch: number, on: boolean, timeMs: number): boolean {
    return this.layer.note(pitch, on, timeMs);
  }

  /** Re-arm the layer when the live-strip toggle changes. */
  setLiveKeysEnabled(on: boolean): void {
    this.layer.setConfig({ ...DEFAULT_PIANO_CONTROL, liveKeysEnabled: on });
  }

  /** On-key glyphs for the D-pad, drawn only while in command mode. */
  commandKeyLabels(): ReadonlyMap<number, string> {
    return this.active ? NAV_GLYPHS : NO_GLYPHS;
  }

  private runLiveAction(action: LiveAction): void {
    switch (action) {
      case "restart":
        this.host.restart();
        break;
      case "loopCycle":
        this.host.loopCycle();
        break;
      case "playPause":
        this.host.playPause();
        break;
      case "tempoDown":
        this.host.nudgeTempo(-0.05);
        break;
      case "tempoUp":
        this.host.nudgeTempo(0.05);
        break;
      case "playForMe":
        this.host.playForMe();
        break;
      case "toggleCoach":
        this.host.toggleCoach();
        break;
      case "toggleMetronome":
        this.host.toggleMetronome();
        break;
      case "toggleRecord":
        this.host.toggleRecord();
        break;
    }
  }
}
